import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Image as ImageIcon, FileVideo, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useUpload } from "@/lib/useUpload";

// Keep these in sync with /app/backend/cloudinary_service.py (MAX_*_BYTES, ALLOWED_*_MIME).
const LIMITS = {
  image: { max_mb: 25, label: "JPG, PNG, WEBP, AVIF, HEIC up to 25 MB" },
  video: { max_mb: 100, label: "MP4, MOV, WEBM up to 100 MB" },
  both:  { max_mb: 100, label: "Image up to 25 MB or video up to 100 MB" },
};

/**
 * Drag-drop / click upload dropzone.
 *
 * Props:
 *  - folder: e.g. "mostash/gallery"
 *  - accept: "image" | "video" | "both"
 *  - value: existing { url, public_id, resource_type? }
 *  - onChange: (asset|null) => void  — asset = { url, public_id, resource_type, ... }
 *  - label: caption above
 *  - height: tailwind aspect class for the preview (default aspect-[4/5])
 */
export default function MediaDropzone({
  folder = "mostash/general",
  accept = "image",
  value,
  onChange,
  label = "Upload",
  height = "aspect-[4/5]",
  testid = "media-dropzone",
}) {
  const { upload, uploading, progress } = useUpload();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  // {kind: 'config'|'auth'|'error', message: string} | null
  const [errorBanner, setErrorBanner] = useState(null);

  useEffect(() => {
    queueMicrotask(() => setErrorBanner(null));
  }, [value]);

  const limit = LIMITS[accept] || LIMITS.image;

  const acceptAttr =
    accept === "image"
      ? "image/*"
      : accept === "video"
      ? "video/mp4,video/quicktime,video/webm"
      : "image/*,video/mp4,video/quicktime,video/webm";

  const handleFile = async (file) => {
    if (!file) return;
    setErrorBanner(null);
    if (accept === "image" && !file.type.startsWith("image/")) {
      toast.error("Only image files are allowed here.");
      return;
    }
    if (accept === "video" && !file.type.startsWith("video/")) {
      toast.error("Only video files are allowed here.");
      return;
    }
    try {
      const asset = await upload(file, { folder });
      onChange?.(asset);
      toast.success("Upload complete");
    } catch (e) {
      const code = e?.code || "UNKNOWN";
      const message = e?.message || "Upload failed";
      if (code === "NOT_CONFIGURED") {
        setErrorBanner({ kind: "config", message: "Cloudinary credentials are missing. Add them in Admin → Settings → Integrations." });
      } else if (code === "AUTH") {
        setErrorBanner({ kind: "auth", message: "Your session expired — please log in again." });
      } else {
        setErrorBanner({ kind: "error", message });
      }
      toast.error(message);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const clear = () => onChange?.(null);

  const isVideo = value?.resource_type === "video";

  return (
    <div data-testid={testid}>
      {label ? (
        <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/55 mb-2">{label}</div>
      ) : null}

      {value?.url ? (
        <div className={`relative w-full ${height} bg-sage/30 border border-beige overflow-hidden group`}>
          {isVideo ? (
            <video src={value.url} className="h-full w-full object-cover" muted playsInline autoPlay loop />
          ) : (
            <img src={value.url} alt="Uploaded" className="h-full w-full object-cover" />
          )}
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-remove`}
            aria-label="Remove"
            className="absolute top-2 right-2 h-9 w-9 bg-ivory/95 text-charcoal border border-beige inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-700 hover:text-ivory hover:border-rose-700"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testid}-replace`}
            className="absolute bottom-2 right-2 h-9 px-3 bg-ivory/95 text-charcoal border border-beige inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-teal-600 hover:text-ivory"
          >
            <Upload className="h-3 w-3" /> Replace
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid={`${testid}-trigger`}
          className={`w-full ${height} bg-sage/20 border border-dashed transition-colors flex flex-col items-center justify-center text-charcoal/55 p-6 ${
            dragging ? "border-teal-600 bg-sage/40 text-teal-600" : "border-beige hover:border-teal-600 hover:text-teal-600"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-7 w-7 animate-spin mb-3" />
              <div className="text-sm">Uploading&hellip; {progress}%</div>
              <div className="mt-2 h-1 w-40 bg-beige overflow-hidden">
                <div className="h-full bg-teal-600 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              {accept === "video" ? <FileVideo className="h-7 w-7 mb-3" /> : <ImageIcon className="h-7 w-7 mb-3" />}
              <div className="font-display text-lg">Drop {accept === "video" ? "a video" : accept === "both" ? "a file" : "an image"} here</div>
              <div className="text-[11px] mt-1 text-charcoal/55 text-center px-3">or click to choose</div>
              <div className="text-[10px] mt-2 text-charcoal/45 uppercase tracking-[0.18em] text-center">{limit.label}</div>
              <div className="text-[10px] mt-1 text-charcoal/35">Folder: {folder.replace("mostash/", "")}</div>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {errorBanner ? (
        <div
          className={`mt-3 flex items-start gap-2 p-3 text-xs ${errorBanner.kind === "config"
            ? "text-rose-700 bg-rose-50 border border-rose-200"
            : "text-amber-800 bg-amber-50 border border-amber-300"
            }`}
          data-testid={`${testid}-error`}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            {errorBanner.message}
            {errorBanner.kind === "config" ? (
              <> {" "}<a href="/admin/settings" className="underline hover:text-rose-900">Open Settings</a>.</>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
